/** Melee attack skill for enemies — config-driven windup/active/recovery/cooldown sequence. */
import { PhasedSkillEffect } from '../PhasedSkillEffect';
import type { SkillPhase } from '../PhasedSkillEffect';
import type { EnemySkillEffect, EnemySkillMovement, EnemySkillDamageInfo } from './EnemySkillEffect';

export interface EnemyMeleeConfig {
  windupMs: number;
  activeDurationMs: number;
  recoveryMs: number;
  cooldownMs: number;
  attackRange: number;
  baseDamage: number;
  attackType: string;
  interruptibleDuringWindup: boolean;
}

/** Close-range melee attack with configurable windup, active, and recovery phases. */
export class EnemyMeleeSkillEffect extends PhasedSkillEffect implements EnemySkillEffect {
  readonly skillId: string;
  protected readonly phaseSequence = ['windup', 'active', 'recovery', 'cooldown'] as const;
  private readonly config: EnemyMeleeConfig;

  constructor(skillId: string, config: EnemyMeleeConfig) {
    super();
    this.skillId = skillId;
    this.config = config;
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return this.config.activeDurationMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  get interruptible(): boolean {
    switch (this.phase) {
      case 'windup': return this.config.interruptibleDuringWindup;
      case 'active': return false;
      case 'recovery': return true;
      default: return true;
    }
  }

  get skillRange(): number { return this.config.attackRange; }

  isInDamagePhase(): boolean { return this.phase === 'active'; }

  getMovement(): EnemySkillMovement | null { return null; }

  getDamageInfo(): EnemySkillDamageInfo | null {
    if (this.phase !== 'active') return null;
    return {
      baseDamage: this.config.baseDamage,
      attackType: this.config.attackType,
      range: this.config.attackRange,
    };
  }
}
